import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { UserRoundX } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { AttendantLoad } from '@aeris/contracts'
import { responseTimeText } from '@aeris/status-meta'
import {
  Button,
  CheckboxField,
  Field,
  InlineAlert,
  ListToolbar,
  Page,
  PageHeader,
  Section,
  Select,
  Spinner,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  cn,
} from '@aeris/ui'

import { PeriodPicker } from '../components/period-picker.tsx'
import {
  assignConversations,
  fetchAttendanceMetrics,
} from '../lib/api/communication-attendance.ts'
import { fetchConversations } from '../lib/api/communication-conversations.ts'
import { fetchChannelAccounts } from '../lib/api/communication.ts'
import { fetchSalespeople } from '../lib/api/team.ts'
import { contactLabelOf } from '../lib/inbox.ts'
import {
  customRange,
  exclusiveEnd,
  periodRange,
  type PeriodChoice,
} from '../lib/period-range.ts'
import { timeAgo } from '../lib/time-ago.ts'

/**
 * O painel do atendimento — F210.
 *
 * O dono: *"se o gerente vê que está pesado para um, distribui"*. A tabela é
 * a carga de cada atendente agora (abertas, esperando resposta e há quanto
 * tempo) e o trabalho do período (conversas respondidas, mensagens, a
 * primeira resposta). Clicar numa linha abre as conversas abertas daquela
 * pessoa — ou as sem responsável — para passar a outra.
 */

// A duração legível mora em `@aeris/status-meta` desde a F308.
const duration = responseTimeText

const NONE = 'none'

export const AtendimentoPainelPage = (): ReactNode => {
  const [period, setPeriod] = useState<PeriodChoice>('today')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [accountId, setAccountId] = useState('')
  /** A linha aberta: um atendente, `none` (sem responsável), ou nenhuma. */
  const [openRow, setOpenRow] = useState<string | null>(null)

  const range =
    period === 'custom'
      ? customRange(customFrom, customTo)
      : periodRange(period)

  const accounts = useQuery({
    queryKey: ['communication-channel-accounts'],
    queryFn: async ({ signal }) => await fetchChannelAccounts(signal),
  })

  const metrics = useQuery({
    queryKey: ['attendance-metrics', range, accountId],
    queryFn: async ({ signal }) =>
      await fetchAttendanceMetrics(
        {
          from: range?.from ?? new Date().toISOString(),
          to: exclusiveEnd(range?.to ?? new Date().toISOString()),
          ...(accountId === '' ? {} : { accountId }),
        },
        signal
      ),
    enabled: range !== null,
    placeholderData: keepPreviousData,
    // O painel fica aberto no computador do gerente: a carga muda sozinha.
    refetchInterval: 60_000,
  })

  const data = metrics.data
  const loading = metrics.isPending
  const error = metrics.isError

  return (
    <Page>
      <PageHeader
        title="Painel do atendimento"
        description="A carga de cada atendente e o tempo de resposta. Clique numa linha para passar conversas a outra pessoa."
      />

      <ListToolbar>
        <PeriodPicker
          choice={period}
          onChoiceChange={setPeriod}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
        />
        {(accounts.data?.length ?? 0) > 1 ? (
          <Field label="Número">
            {(field) => (
              <Select
                {...field}
                value={accountId}
                onChange={(event) => {
                  setAccountId(event.target.value)
                }}
              >
                <option value="">Todos os números</option>
                {(accounts.data ?? []).map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.displayName}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}
      </ListToolbar>

      <section
        aria-label="Resumo do atendimento"
        className="grid grid-cols-2 gap-2 lg:grid-cols-4"
      >
        <StatTile
          label="Conversas abertas"
          loading={loading}
          error={error}
          value={<span className="tabular-nums">{data?.totals.open ?? 0}</span>}
        />
        <StatTile
          label="Esperando resposta"
          loading={loading}
          error={error}
          value={
            <span
              className={cn(
                'tabular-nums',
                (data?.totals.waiting ?? 0) > 0 && 'text-(--warning)'
              )}
            >
              {data?.totals.waiting ?? 0}
            </span>
          }
        />
        <StatTile
          label="Sem responsável"
          loading={loading}
          error={error}
          value={
            <span
              className={cn(
                'tabular-nums',
                (data?.totals.unassigned ?? 0) > 0 && 'text-(--danger)'
              )}
            >
              {data?.totals.unassigned ?? 0}
            </span>
          }
          hint="Nenhum dono no número e nenhum vendedor no cliente."
        />
        <StatTile
          label="Espera mais antiga"
          loading={loading}
          error={error}
          value={
            data?.totals.oldestWaitingAt == null
              ? '—'
              : timeAgo(data.totals.oldestWaitingAt)
          }
        />
      </section>

      <Section title="Por atendente">
        <TableContainer>
          <Table aria-label="Carga por atendente">
            <TableHead>
              <TableRow>
                <TableHeaderCell>Atendente</TableHeaderCell>
                <TableHeaderCell className="text-right">
                  Abertas
                </TableHeaderCell>
                <TableHeaderCell className="text-right">
                  Esperando
                </TableHeaderCell>
                <TableHeaderCell>Espera mais antiga</TableHeaderCell>
                <TableHeaderCell className="text-right">
                  Respondidas
                </TableHeaderCell>
                <TableHeaderCell className="text-right">
                  Mensagens
                </TableHeaderCell>
                <TableHeaderCell>1ª resposta</TableHeaderCell>
                <TableHeaderCell className="text-right">
                  Carteira
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.attendants ?? []).map((row) => {
                const key = row.userId ?? NONE
                const open = openRow === key
                return (
                  <AttendantRow
                    key={key}
                    row={row}
                    open={open}
                    onToggle={() => {
                      setOpenRow(open ? null : key)
                    }}
                  />
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <p className="text-(length:--text-xs) text-(--text-muted)">
          1ª resposta: a mediana do tempo entre a mensagem do cliente e a
          primeira resposta de uma pessoa — pelo Aeris ou pelo celular. O robô
          não conta. Carteira: clientes em que ela é o vendedor.
        </p>
      </Section>

      {openRow === null ? null : (
        <Redistribute
          assignee={openRow}
          accountId={accountId === '' ? undefined : accountId}
          people={data?.attendants ?? []}
          onClose={() => {
            setOpenRow(null)
          }}
        />
      )}
    </Page>
  )
}

const AttendantRow = ({
  row,
  open,
  onToggle,
}: {
  row: AttendantLoad
  open: boolean
  onToggle: () => void
}): ReactNode => (
  <TableRow
    aria-selected={open}
    className={cn(
      'cursor-pointer',
      open && 'bg-(--surface-sunken)',
      row.userId === null && row.open > 0 && 'bg-(--danger-subtle)'
    )}
    onClick={onToggle}
  >
    <TableCell>
      <button
        type="button"
        className="flex items-center gap-1.5 text-left font-(--weight-semibold) underline decoration-dotted underline-offset-2"
        onClick={(event) => {
          event.stopPropagation()
          onToggle()
        }}
      >
        {row.userId === null ? (
          <>
            <UserRoundX aria-hidden="true" className="h-4 w-4" />
            Sem responsável
          </>
        ) : (
          (row.name ?? 'Alguém da equipe')
        )}
      </button>
    </TableCell>
    <TableCell className="text-right tabular-nums">{row.open}</TableCell>
    <TableCell
      className={cn(
        'text-right tabular-nums',
        row.waiting > 0 && 'font-(--weight-semibold) text-(--warning)'
      )}
    >
      {row.waiting}
    </TableCell>
    <TableCell className="text-(--text-secondary)">
      {row.oldestWaitingAt === null ? '—' : timeAgo(row.oldestWaitingAt)}
    </TableCell>
    <TableCell className="text-right tabular-nums">
      {row.userId === null ? '—' : row.repliedConversations}
    </TableCell>
    <TableCell className="text-right tabular-nums">
      {row.userId === null ? '—' : row.messagesSent}
    </TableCell>
    <TableCell className="tabular-nums">
      {duration(row.medianFirstResponseSeconds)}
    </TableCell>
    <TableCell className="text-right tabular-nums">
      {row.userId === null ? '—' : row.portfolio}
    </TableCell>
  </TableRow>
)

/**
 * As conversas abertas de uma pessoa, para passar a outra (F210). Todas
 * marcadas de começo: o caso comum é "tira tudo dela", e desmarcar a que fica
 * é um clique.
 */
const Redistribute = ({
  assignee,
  accountId,
  people,
  onClose,
}: {
  assignee: string
  accountId: string | undefined
  people: readonly AttendantLoad[]
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const [kept, setKept] = useState<ReadonlySet<string>>(new Set())
  const [target, setTarget] = useState('')
  const [notice, setNotice] = useState<string | null>(null)

  const conversations = useQuery({
    queryKey: ['attendance-conversations', assignee, accountId ?? ''],
    queryFn: async ({ signal }) =>
      await fetchConversations(
        {
          limit: 100,
          offset: 0,
          assignee,
          ...(accountId === undefined ? {} : { accountId }),
        },
        signal
      ),
  })

  const everyone = useQuery({
    queryKey: ['salespeople'],
    queryFn: async ({ signal }) => await fetchSalespeople(signal),
  })

  const items = conversations.data?.conversations ?? []
  const chosen = items.filter((item) => !kept.has(item.id))
  const who =
    assignee === NONE
      ? 'Sem responsável'
      : (people.find((row) => row.userId === assignee)?.name ?? 'Atendente')

  const move = useMutation({
    // O erro aparece na própria tela; o aviso geral não repete (F248).

    meta: { errorShownInline: true },
    mutationFn: async () =>
      await assignConversations({
        conversationIds: chosen.map((item) => item.id),
        employeeId: target === NONE ? null : target,
      }),
    onSuccess: async (result) => {
      const name =
        target === NONE
          ? 'sem responsável'
          : (everyone.data?.items.find((person) => person.userId === target)
              ?.displayName ?? 'a outra pessoa')
      setNotice(
        `${String(result.assigned)} ${result.assigned === 1 ? 'conversa passou' : 'conversas passaram'} para ${name}.`
      )
      setKept(new Set())
      await queryClient.invalidateQueries({
        queryKey: ['attendance-conversations'],
      })
      await queryClient.invalidateQueries({ queryKey: ['attendance-metrics'] })
      void queryClient.invalidateQueries({
        queryKey: ['communication-conversations'],
      })
    },
  })

  return (
    <Section
      title={`Conversas abertas — ${who}`}
      action={
        <Button variant="ghost" size="sm" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      {conversations.isPending ? (
        <Spinner label="Carregando as conversas" />
      ) : items.length === 0 ? (
        <p className="text-(length:--text-sm) text-(--text-muted)">
          Nenhuma conversa aberta aqui.
        </p>
      ) : (
        <>
          <ul
            aria-label="Conversas para passar"
            className="grid gap-1 sm:grid-cols-2"
          >
            {items.map((item) => (
              <li
                key={item.id}
                className="rounded-(--radius-md) border border-(--border-subtle) px-3 py-2"
              >
                <CheckboxField
                  label={
                    <span className="flex flex-col">
                      <span>{contactLabelOf(item)}</span>
                      <span className="text-(length:--text-xs) text-(--text-muted)">
                        {item.lastMessageAt === null
                          ? ''
                          : timeAgo(item.lastMessageAt)}
                      </span>
                    </span>
                  }
                  checked={!kept.has(item.id)}
                  onCheckedChange={(checked) => {
                    const next = new Set(kept)
                    if (checked === true) next.delete(item.id)
                    else next.add(item.id)
                    setKept(next)
                  }}
                />
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-end gap-2">
            <Field label="Passar para">
              {(field) => (
                <Select
                  {...field}
                  value={target}
                  onChange={(event) => {
                    setTarget(event.target.value)
                  }}
                >
                  <option value="">Escolha…</option>
                  {(everyone.data?.items ?? [])
                    .filter((person) => person.userId !== assignee)
                    .map((person) => (
                      <option key={person.userId} value={person.userId}>
                        {person.displayName}
                      </option>
                    ))}
                  {assignee === NONE ? null : (
                    <option value={NONE}>Sem responsável</option>
                  )}
                </Select>
              )}
            </Field>
            <Button
              variant="primary"
              disabled={target === '' || chosen.length === 0}
              loading={move.isPending}
              onClick={() => {
                move.mutate()
              }}
            >
              {chosen.length === 1
                ? 'Passar 1 conversa'
                : `Passar ${String(chosen.length)} conversas`}
            </Button>
          </div>
        </>
      )}
      {notice === null ? null : (
        <InlineAlert tone="success">{notice}</InlineAlert>
      )}
      {move.isError ? (
        <InlineAlert tone="danger">Não foi possível passar agora.</InlineAlert>
      ) : null}
    </Section>
  )
}
